import { memo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { useOutsideClick } from "../../../utils";

import styles from "./styles.module.scss";

export type ContextMenuPositionType = {
  x: number;
  y: number;
}

export enum ContextMenuSizeBreakpoints {
  s = "small",
  m = "medium",
  l = "large"
}

export type ContextMenuOption = {
  label: string,
  onClick: () => void
}

export interface ContextMenuProps {
  size?: ContextMenuSizeBreakpoints
  position: ContextMenuPositionType | null,
  shouldCloseOnEscape?: boolean,
  onClose: () => void,
  options: ContextMenuOption[],
}

export const ContextMenu = memo<ContextMenuProps>((
  {
    size = ContextMenuSizeBreakpoints.m,
    shouldCloseOnEscape = true,
    options,
    onClose,
    position,
  }) => {
  const contextMenuRef = useRef<HTMLUListElement | null>(null);

  const handleEscapeClick = (e: KeyboardEvent) => {
    if (e.key === "Escape" && shouldCloseOnEscape) {
      onClose();
    }
  };
  const handleScroll = (e: WheelEvent) => {
    if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
      onClose?.();
    }
  };
  const handleClick = (e: MouseEvent) => {
    if (contextMenuRef?.current?.contains(e.target as Node)) {
      onClose?.();
    }
  };

  useOutsideClick(contextMenuRef, onClose);
  useEffect(() => {
    document.addEventListener("keydown", handleEscapeClick);
    document.addEventListener("wheel", handleScroll);
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("keydown", handleEscapeClick);
      document.removeEventListener("wheel", handleScroll);
      document.removeEventListener("click", handleClick);
    };
  });

  return (
    position
      ?
      createPortal(
        <ul
          ref={contextMenuRef}
          className={classNames(styles.menu, styles[size])}
          style={{ top: position.y, left: position.x }}
        >
          {
            options.map((option, index) => <li key={index} onClick={option.onClick}>{option.label}</li>)
          }
        </ul>,
        document.querySelector("#popperPortal") as any,
      )

      :
      null);
});

export default ContextMenu;
